import type { ApolloLink } from '@apollo/client';
import { Decimal } from 'decimal.js';
import { GraphQLSchema, buildClientSchema, type IntrospectionQuery } from 'graphql';
import { withScalars } from 'apollo-link-scalars';

export interface CreateDecimalLinkOptions {
  /**
   * Either a built GraphQLSchema, or an introspection query result
   * (e.g. JSON produced by @graphql-codegen/introspection).
   */
  schema: GraphQLSchema | IntrospectionQuery;
}

/**
 * Apollo Link that translates the `Decimal` GraphQL scalar to/from
 * `decimal.js` instances on the client.
 *
 * - Outgoing variables: `Decimal` → string (full precision via `toString()`)
 * - Incoming response data: string | number → `Decimal`
 *
 * Place this link before the terminating HttpLink in the chain.
 */
export function createDecimalLink({ schema }: CreateDecimalLinkOptions): ApolloLink {
  const resolvedSchema = schema instanceof GraphQLSchema ? schema : buildClientSchema(schema);

  return withScalars({
    schema: resolvedSchema,
    typesMap: {
      Decimal: {
        serialize: (parsed: unknown): string | null => {
          if (parsed === null || parsed === undefined) return null;
          if (parsed instanceof Decimal) return parsed.toString();
          if (typeof parsed === 'string' || typeof parsed === 'number') {
            return new Decimal(parsed).toString();
          }
          throw new TypeError(`Decimal scalar: cannot serialize value of type ${typeof parsed}`);
        },
        parseValue: (raw: unknown): Decimal | null => {
          if (raw === null || raw === undefined) return null;
          if (raw instanceof Decimal) return raw;
          if (typeof raw === 'string' || typeof raw === 'number') return new Decimal(raw);
          throw new TypeError(`Decimal scalar: cannot parse value of type ${typeof raw}`);
        }
      }
    }
  });
}
